import i18n from '@/configuration/i18n/i18n.configuration';
import RouteNames from '@/configuration/router/names.routes';
import { ErrorWithToastData } from '@/exceptions/ErrorWithToastData';
import navigationService from '@/services/NavigationService';
import { useToasterStore } from '@/stores/useToasterStore';

/**
 * Generic validation rule executor for router guards
 * @param validationFn - The validation function to execute
 * @param routeName - The name of the route being accessed (for error logging)
 * @param fallbackRouteName - The name of the route to navigate to if validation fails (optional, defaults to Home)
 * @returns Promise<boolean> - true if validation passes, false otherwise
 */
const validateRule = async (
  validationFn: () => Promise<void>,
  routeName: string,
  fallbackRouteName?: string
): Promise<boolean> => {
  try {
    await validationFn();
    return true;
  } catch (error) {
    console.error(`Validation failed for route '${routeName}':`, error);

    const toaster = useToasterStore();
    if (error instanceof ErrorWithToastData) {
      toaster.error({
        title: error.title,
        body: error.body
      });
    } else {
      const { t } = i18n.global;
      toaster.error({
        title: t('contextual_errors.unknown.title'),
        body: t('contextual_errors.unknown.body')
      });
    }

    navigationService.navigateTo({
      name: fallbackRouteName || RouteNames.Home
    });
    return false;
  }
};

export const RouterValidationUtils = {
  validateRule
};
